import React, { useState } from "react";
import {
  ShareNetwork, Copy, Check, Eye, QrCode, House, Users,
  Trash, ArrowSquareOut, WhatsappLogo, Palette, ArrowsLeftRight,
  DeviceMobile, Desktop, ArrowClockwise, X, ArrowsOutSimple,
} from "@phosphor-icons/react";
import { usePropertyDetail } from "../context";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { isNative } from "@/lib/native";
import BrandColorPicker from "@/components/BrandColorPicker";
import ListingStyleSwatch from "@/components/ListingStyleSwatch";
import { DismissibleHint } from "@/components/DismissibleHint";
import SectionCoachmark from "@/components/SectionCoachmark";
import { toast } from "sonner";

// iter v28 r42 — Stili pagina online disponibili. Mappati su CSS data-style.
// iter v28 r82 — Sub aggiornati per riflettere i layout attuali (post r71-r81).
// iter v28 r127 — User M1045: aggiornati ai 4 layout social V2 (Moderno,
// Elegante, Minimal, Lussuoso). I "vecchi" nomi (luxury) restano supportati
// dal backend come legacy alias.
const LISTING_STYLES = [
  { v: "moderno",  l: "Moderno",  sub: "Archivo Black · banda brand sotto"     },
  { v: "elegante", l: "Elegante", sub: "Bodoni Moda Italic · didone display"  },
  { v: "minimal",  l: "Minimal",  sub: "Split foto/testo · pulizia svizzera"  },
  { v: "lussuoso", l: "Lussuoso", sub: "Spectral fine + oro · sfondo scuro"         },
];

// iter v28 r219 — User M1139: mini-anteprima astratta (barrette/blocchi)
// per i 4 layout della VETRINA online. Stessa estetica di LAYOUT_MINI
// dell'editor social ma calibrata sulla struttura della vetrina.
const LAYOUT_MINI_VETRINA = {
  moderno: [
    // Banda brand bottom + barre testo a sinistra
    { h: "3px", w: "60%", c: "#1C1C1A", a: "flex-start" },
    { h: "5px", w: "85%", c: "#1C1C1A", a: "flex-start" },
    { h: "8px", w: "100%", c: "#7C8B6E", a: "flex-start" },
  ],
  elegante: [
    // Foto framed + titolo italic centrato
    { h: "11px", w: "60%", c: "#D5D2CA", a: "center" },
    { h: "3px", w: "50%", c: "#7C8B6E", a: "center" },
    { h: "2px", w: "35%", c: "#7C8B6E", a: "center" },
  ],
  minimal: [
    // Split foto/testo (50/50 dividing line)
    { h: "11px", w: "45%", c: "#D5D2CA", a: "flex-start" },
    { h: "3px", w: "30%", c: "#1C1C1A", a: "flex-end" },
    { h: "2px", w: "20%", c: "#7C8B6E", a: "flex-end" },
  ],
  lussuoso: [
    // Dark mode con oro
    { h: "9px", w: "55%", c: "#1C1B1A", a: "center" },
    { h: "2px", w: "70%", c: "#C8B886", a: "center" },
    { h: "3px", w: "30%", c: "#C8B886", a: "center" },
  ],
};

export default function CondivisioneTab() {
  const {
    id, property, photos, realPhotos, listing, stats, linkCopied,
    onActivateListing, onToggleListing, onDeleteListing,
    copyPublicLink, downloadQr, downloadQrWa, publicListingUrl, publicShareUrl, onOpenShare, setTab, setProperty,
  } = usePropertyDetail();
  const { user, refresh } = useAuth();

  // iter v28 r42 — Personalizzazione pagina online: stile + brand color.
  const [savingStyle, setSavingStyle] = useState(false);
  const effectiveStyle = property?.listing_style || user?.default_listing_style || "moderno";
  const brandColor = user?.brand_color || "#7C8B6E";

  // iter v28 r220 — User M1140: counter esplicito per il refresh dell'anteprima
  // live. Si incrementa SOLO dopo che il save backend è confermato → l'iframe
  // ricarica con la garanzia che il GET listing pubblico vedrà il nuovo valore.
  const [previewBust, setPreviewBust] = useState(Date.now());

  const setListingStyle = async (next) => {
    if (savingStyle || next === effectiveStyle) return;
    setSavingStyle(true);
    try {
      const { data } = await api.put(`/properties/${id}`, { listing_style: next });
      setProperty((prev) => ({ ...prev, ...data, listing_style: next }));
      toast.success("Stile pagina aggiornato");
      setPreviewBust(Date.now());
    } catch (e) {
      toast.error("Impossibile salvare lo stile");
    } finally {
      setSavingStyle(false);
    }
  };

  const [savingColor, setSavingColor] = useState(false);
  const setBrandColor = async (hex) => {
    if (savingColor) return;
    setSavingColor(true);
    try {
      await api.put("/profile/agency", { brand_color: hex });
      await refresh();
      toast.success("Colore brand aggiornato");
      setPreviewBust(Date.now());
    } catch (e) {
      toast.error("Impossibile salvare il colore");
    } finally {
      setSavingColor(false);
    }
  };

  // iter v28 r59 — Toggle "Mostra prezzo nella pagina online"
  // Salva property.hide_price (true → "Trattativa riservata", false → € N).
  const [savingHidePrice, setSavingHidePrice] = useState(false);
  const hidePrice = !!property?.hide_price;
  const togglePublicPrice = async () => {
    if (savingHidePrice) return;
    setSavingHidePrice(true);
    const next = !hidePrice;
    try {
      await api.put(`/properties/${id}`, { hide_price: next });
      setProperty((prev) => ({ ...prev, hide_price: next }));
      toast.success(next ? "Prezzo nascosto dalla vetrina" : "Prezzo mostrato sulla vetrina");
    } catch (e) {
      toast.error("Impossibile salvare la preferenza");
    } finally {
      setSavingHidePrice(false);
    }
  };

  // iter v28 r69 — Modalità Prima/Dopo: slider | only_before | only_after
  // Globale per tutte le coppie. Decide come la galleria pubblica le rende.
  const [savingBAMode, setSavingBAMode] = useState(false);
  const baMode = property?.before_after_mode || "slider";
  const setBAMode = async (next) => {
    if (savingBAMode || next === baMode) return;
    setSavingBAMode(true);
    try {
      await api.put(`/properties/${id}`, { before_after_mode: next });
      setProperty((prev) => ({ ...prev, before_after_mode: next }));
      toast.success(
        next === "slider" ? "Galleria con slider Prima·Dopo" :
        next === "only_before" ? "Galleria con SOLO foto Prima" :
        "Galleria con SOLO foto Dopo"
      );
    } catch (e) {
      toast.error("Impossibile salvare la preferenza");
    } finally {
      setSavingBAMode(false);
    }
  };

  // iter 275 round 51: guardrail per le schede "Senza annuncio" (ad-hoc
  // create dal Social Studio → Galleria). Non hanno dati completi per
  // costruire una landing pubblica credibile, quindi blocchiamo la
  // pubblicazione e indirizziamo l'utente a creare un annuncio vero.
  if (property?.is_ad_hoc) {
    return (
      <div className="mt-8 max-w-2xl">
        <div
          className="card-flat p-6 sm:p-8 border border-dashed border-[#E8D9A8] bg-[#FFFAEC]"
          data-testid="adhoc-no-public-page"
        >
          <span className="overline">Scheda senza annuncio</span>
          <h3 className="font-editorial text-2xl mt-2 mb-3">
            La pagina pubblica è <span className="italic-accent text-[#7C8B6E]">solo per annunci completi</span>
          </h3>
          <p className="text-sm text-[#6B6A65] mb-5 leading-relaxed">
            Questa scheda è stata creata dall&apos;Editor social per produrre contenuti rapidi
            a partire dalle tue foto. Non ha tutti i dati necessari per una landing
            pubblica credibile (descrizione, mq, classe energetica, indirizzo).
            <br /><br />
            Trasformala in un annuncio completo e potrai attivare la pagina pubblica,
            raccogliere contatti e condividerla sui portali.
          </p>
          <a
            href="/dashboard/annunci/nuovo"
            className="btn-primary inline-flex items-center"
            data-testid="adhoc-convert-to-listing"
          >
            <House size={16} weight="bold" /> Crea l'annuncio completo
          </a>
        </div>
      </div>
    );
  }

  return (
      <div className="mt-8 max-w-3xl mx-auto">
        {/* iter v33 r9 · User M1486 — Coachmark uniforme "Cosa puoi fare qui"
            che sostituisce il vecchio DismissibleHint. Spiega in 3 mosse
            cosa è la vetrina personale del listing (non i portali). */}
        <SectionCoachmark
          id="property-condivisione"
          title="La pagina online di questo annuncio"
          steps={[
            {
              icon: <Palette size={15} weight="bold" />,
              title: "Personalizza l'aspetto",
              body: "Scegli lo stile della pagina e il colore brand: si applicano subito nell'anteprima live.",
            },
            {
              icon: <ArrowsLeftRight size={15} weight="bold" />,
              title: "Attiva la pagina",
              body: "Un click e l'annuncio è online su prontoimmo.it/listing/… con foto, descrizione, prezzo.",
            },
            {
              icon: <ShareNetwork size={15} weight="bold" />,
              title: "Condividi",
              body: "Copi il link e lo mandi via WhatsApp, email o social. Chi lo apre ti lascia i contatti.",
            },
          ]}
        />

        {/* ═══════ EDITOR · stile, colore, prezzo, prima/dopo ═══════ */}
        <div className="card-flat p-5 sm:p-6" data-testid="public-customize">
          <div className="flex items-center gap-2 mb-1">
            <Palette size={14} weight="bold" className="text-[#7C8B6E]" />
            <span className="overline">1. Personalizza l'aspetto</span>
          </div>
          <p className="text-[12px] text-[#6B6A65] mb-4">
            Imposta come appare la vetrina prima di condividerla.
          </p>

          {/* iter v28 r68 — Toggle "Mostra prezzo" SOPRA lo style selector.
              iter v28 r94 — Visibile SOLO se l'annuncio ha un prezzo
              impostato (altrimenti la toggle non serve). */}
          {Number(property?.price) > 0 && (
          <button
            type="button"
            role="switch"
            aria-checked={!hidePrice}
            disabled={savingHidePrice}
            onClick={togglePublicPrice}
            className={`w-full flex items-center justify-between gap-4 p-3 rounded-lg border transition-colors text-left ${
              !hidePrice
                ? "border-[#7C8B6E] bg-[#F4F6F0]"
                : "border-[#E5E7E0] bg-white hover:bg-[#FAFAF7]"
            } ${savingHidePrice ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
            data-testid="toggle-public-price"
          >
            <div className="flex-1 min-w-0">
              <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">
                Mostra il prezzo sulla vetrina personale
              </div>
              <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
                Se disattivi, sulla vetrina appare <em>"Trattativa riservata"</em>.
              </p>
            </div>
            <span
              aria-hidden="true"
              className={`relative flex-shrink-0 w-11 h-6 rounded-full transition-colors ${
                !hidePrice ? "bg-[#7C8B6E]" : "bg-[#D7D5CB]"
              }`}
            >
              <span
                className="absolute top-0.5 w-5 h-5 rounded-full bg-white shadow-sm transition-transform"
                style={{ transform: !hidePrice ? "translateX(22px)" : "translateX(2px)" }}
              />
            </span>
          </button>
          )}

          {/* Modalità Prima/Dopo */}
          <div className={`${Number(property?.price) > 0 ? "mt-4" : ""} p-3 rounded-lg border border-[#E5E7E0] bg-white`} data-testid="ba-mode-control">
            <div className="flex items-start gap-2 mb-3">
              <ArrowsLeftRight size={14} weight="bold" className="text-[#7C8B6E] mt-0.5 flex-shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-[13px] font-semibold text-[#1C1C1A] leading-tight">
                  Foto Prima/Dopo nella galleria
                </div>
                <p className="text-[11.5px] text-[#6B6A65] leading-snug mt-1">
                  Se hai caricato coppie di foto di <strong>restyling/ristrutturazione</strong> (es. stanza
                  vuota → stanza arredata, locale grezzo → locale finito), scegli come la pagina
                  pubblica le mostra.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-1.5 p-1 rounded-md bg-[#F4F2EC]" role="radiogroup" aria-label="Modalità Prima/Dopo">
              {[
                { v: "slider", l: "Slider", sub: "Prima·Dopo trascinabile" },
                { v: "only_before", l: "Solo Prima", sub: "Mostra le originali" },
                { v: "only_after", l: "Solo Dopo", sub: "Mostra le arredate" },
              ].map(({ v, l, sub }) => {
                const active = baMode === v;
                return (
                  <button
                    key={v}
                    type="button"
                    role="radio"
                    aria-checked={active}
                    disabled={savingBAMode}
                    onClick={() => setBAMode(v)}
                    className={`px-2 py-2 rounded text-center transition-all ${
                      active
                        ? "bg-white text-[#1C1C1A] shadow-sm font-semibold"
                        : "text-[#6B6A65] hover:text-[#1C1C1A]"
                    } ${savingBAMode ? "opacity-60 cursor-wait" : "cursor-pointer"}`}
                    data-testid={`ba-mode-${v}`}
                  >
                    <div className="text-[12px] leading-tight">{l}</div>
                    <div className={`text-[10px] leading-tight mt-0.5 ${active ? "text-[#6B6A65]" : "text-[#9A968B]"}`}>{sub}</div>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="flex items-center gap-2 mt-5 mb-3">
            <Palette size={14} weight="bold" className="text-[#7C8B6E]" />
            <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
              Colore brand
            </div>
          </div>

          <div>
            <BrandColorPicker
              value={brandColor}
              onChange={setBrandColor}
              variant="circle"
            />
            <p className="mt-2 text-[11px] text-[#6B6A65] leading-snug">
              Copertina della vetrina e contenuti social.
            </p>
          </div>

          {/* iter v28 r219 — User M1139: stile vetrina IDENTICO all'editor
              social (mini-barre astratte invece di mini-swatch fotografici).
              Stesso pattern di StaticStyleSelector + LAYOUT_MINI. */}
          <div className="mt-5 pt-5 border-t border-[#EAE7DF]">
            <div className="flex items-center gap-2 mb-3">
              <Palette size={14} weight="bold" className="text-[#7C8B6E]" />
              <div className="text-[11px] uppercase tracking-[0.18em] font-bold text-[#9A968B]">
                Stile vetrina personale · 4 disponibili
              </div>
            </div>
            <div className="grid grid-cols-4 gap-1.5" data-testid="public-style-grid">
              {LISTING_STYLES.map(({ v, l }) => {
                const active = effectiveStyle === v;
                const miniBars = LAYOUT_MINI_VETRINA[v] || [];
                return (
                  <button
                    key={v}
                    type="button"
                    onClick={() => setListingStyle(v)}
                    disabled={savingStyle}
                    className={`relative p-1.5 rounded-lg border text-left transition-all ${
                      active
                        ? "border-[#7C8B6E] bg-[#F1F4ED]"
                        : "border-[#EAE7DF] hover:border-[#7C8B6E]/40 hover:bg-[#FAFAF7]"
                    }`}
                    data-testid={`listing-style-${v}`}
                    aria-pressed={active}
                    title={l}
                  >
                    <div className="w-full h-7 rounded mb-1 bg-white border border-[#EAE7DF] flex flex-col justify-end p-1 gap-[2px] overflow-hidden">
                      {miniBars.map((bar, i) => (
                        <div
                          key={i}
                          style={{
                            height: bar.h,
                            width: bar.w,
                            background: bar.c,
                            alignSelf: bar.a,
                            borderRadius: 1,
                          }}
                        />
                      ))}
                    </div>
                    <div className="text-[10px] font-semibold text-[#1C1C1A] leading-tight">
                      {l}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* iter v28 r216 — User M1136: Anteprima live mobile/desktop.
              Mostra la vetrina pubblica embedded in un iframe con toggle
              "Mobile / Desktop" e auto-refresh al cambio stile/colore.
              iter v28 r440 — User M1353: «in annunci vetrina online prima
              di pubblicare vorrei vedere l'anteprima live». Quando l'annuncio
              NON è ancora pubblicato, mostriamo comunque l'anteprima ma
              usando l'endpoint owner-only `/listing-preview/{pid}` (vedi
              backend `/api/properties/{pid}/preview-listing`). Così l'agente
              vede esattamente come sarà online prima di cliccare "Pubblica". */}
          {listing?.exists && publicListingUrl ? (
            <PublicListingPreview
              url={publicListingUrl}
              bust={previewBust}
            />
          ) : (
            <div data-testid="prepublish-preview-wrapper">
              <div className="mb-3 px-4 py-2.5 rounded-md bg-[#FFF8E7] border border-[#E8D9A8] flex items-center gap-2">
                <span className="text-[#C49A2A] text-sm leading-none">●</span>
                <div className="text-[12.5px] text-[#7A5C00] leading-relaxed">
                  <strong>Anteprima privata.</strong> Solo tu la vedi. L'annuncio
                  non è ancora online — controlla come apparirà e poi pubblica.
                </div>
              </div>
              <PublicListingPreview
                url={`${window.location.origin}/listing-preview/${id}`}
                bust={previewBust}
              />
            </div>
          )}
        </div>

        {/* ═══════ LINK & CONDIVISIONE · sotto l'editor ═══════ */}
        <div className="card-flat p-6 sm:p-8 mt-4">
            <DismissibleHint id="condivisione-step2-intro" className="mb-5 pr-9">
              <span className="overline">2. Condividi il link</span>
              <h3 className="font-editorial text-2xl mt-2 mb-3">
                Il <span className="italic-accent text-[#7C8B6E]">link condivisibile</span> di questo annuncio
              </h3>
              <p className="text-sm text-[#6B6A65] leading-relaxed">
                Da condividere su WhatsApp, email e social. I clienti aprono il link, vedono l'annuncio
                completo e ti lasciano i contatti per essere ricontattati.
              </p>
            </DismissibleHint>

            {/* iter 261: hint contenuti — se l'utente ha < 4 foto
                enhanced sulla pagina pubblica, lo segnaliamo qui. */}
            {listing?.exists && realPhotos.length < 4 && (
              <div className="mb-5 p-3.5 rounded-md bg-[#FFF8E7] border border-[#E8D9A8]" data-testid="public-photo-hint">
                <div className="flex items-start gap-2.5">
                  <span className="text-[#C49A2A] text-base leading-none mt-0.5">⚑</span>
                  <div className="flex-1 text-[12.5px] text-[#7A5C00] leading-relaxed">
                    <div className="font-semibold mb-0.5">
                      La pagina mostra solo {realPhotos.length} foto.
                    </div>
                    <div>
                      Per un annuncio efficace consigliamo almeno 4–6 foto.
                    </div>
                    <button
                      onClick={() => setTab("foto")}
                      className="mt-1.5 underline font-semibold hover:text-[#5A4400]"
                      data-testid="public-photo-hint-add"
                    >
                      Aggiungi altre foto →
                    </button>
                  </div>
                </div>
              </div>
            )}

            {!listing?.exists ? (
              // iter v28 r60 — Pre-pubblicazione: messaggio chiaro che NON è
              // ancora online finché non clicca. Bottone grande e contrastato.
              // iter v29 r255 — User M1399: «pubblica online non è chiaro».
              // Spiego esplicitamente: pagina condivisibile via link, NON
              // pubblicazione automatica su portali esterni (Immobiliare, Idealista).
              <div className="rounded-lg border-2 border-dashed border-[#7C8B6E] bg-[#F4F6F0] p-5" data-testid="pre-publish-block">
                <div className="flex items-start gap-2 mb-3">
                  <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold px-2 py-1 bg-[#FFF8E7] text-[#7A5C00] border border-[#E8D9A8] rounded-full">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#C49A2A]" /> Non ancora online
                  </span>
                </div>
                <h4 className="font-editorial text-lg text-[#1C1C1A] leading-tight mb-1.5">
                  Crea la <em className="italic-accent text-[#7C8B6E]">pagina condivisibile</em> di questo annuncio
                </h4>
                <p className="text-[13px] text-[#6B6A65] leading-snug mb-2">
                  Generiamo una pagina web su <code className="px-1 py-0.5 rounded bg-white text-[#1C1C1A] text-[12px]">prontoimmo.it/listing/…</code> con foto, descrizione e dati strutturati. Ricevi link + QR code da inviare ai tuoi clienti via WhatsApp, email o social.
                </p>
                <p className="text-[12px] text-[#9A968B] leading-snug mb-4 italic">
                  Non viene pubblicato su Immobiliare.it o Idealista (per quelli scarichi il pacchetto dalla scheda Materiali). Puoi metterla offline in qualsiasi momento.
                </p>
                <button
                  onClick={onActivateListing}
                  className="btn-primary text-base px-6 py-3 w-full sm:w-auto justify-center"
                  data-testid="activate-listing-btn"
                >
                  <ShareNetwork size={18} weight="bold" /> Crea pagina condivisibile · gratuito
                </button>
              </div>
            ) : (
              <>
                {/* iter v28 r60 — Stato ONLINE/OFFLINE molto più esplicito */}
                <div className={`rounded-lg p-4 mb-4 border-2 ${
                  listing.is_public
                    ? "bg-[#F4F6F0] border-[#7C8B6E]"
                    : "bg-[#FFF8E7] border-[#E8D9A8]"
                }`} data-testid="listing-status-card">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`inline-block w-2 h-2 rounded-full ${listing.is_public ? "bg-[#7C8B6E] animate-pulse" : "bg-[#C49A2A]"}`} />
                        <span className={`text-[10.5px] uppercase tracking-[0.24em] font-bold ${listing.is_public ? "text-[#7C8B6E]" : "text-[#7A5C00]"}`} data-testid="listing-status">
                          {listing.is_public ? "Pagina condivisibile attiva" : "Pagina condivisibile spenta"}
                        </span>
                      </div>
                      <div className="text-[13px] text-[#1C1C1A] leading-snug">
                        {listing.is_public
                          ? "Chi apre il link vede l'annuncio. Non è su Immobiliare.it né Idealista — solo su prontoimmo.it/listing/…"
                          : "Il link è temporaneamente disattivato. Riattivalo quando vuoi."}
                      </div>
                    </div>
                    <button
                      onClick={onToggleListing}
                      className={`text-[12.5px] font-semibold px-4 py-2 rounded-full border-2 transition-colors whitespace-nowrap ${
                        listing.is_public
                          ? "border-[#E8D9A8] bg-white text-[#7A5C00] hover:bg-[#FFF8E7]"
                          : "border-[#7C8B6E] bg-[#7C8B6E] text-white hover:bg-[#6B7A5E]"
                      }`}
                      data-testid="toggle-listing-btn"
                    >
                      {listing.is_public ? "Disattiva link" : "Riattiva link"}
                    </button>
                  </div>
                  <div className="mt-3 pt-3 border-t border-current/10 flex items-center gap-4 text-[12px] text-[#6B6A65]" data-testid="stats-inline">
                    <span className="inline-flex items-center gap-1.5"><Eye size={13} /> <strong className="text-[#1C1C1A]">{stats?.views ?? 0}</strong> viste</span>
                    <span className="inline-flex items-center gap-1.5"><Users size={13} /> <strong className="text-[#7C8B6E]">{stats?.leads ?? 0}</strong> richieste</span>
                  </div>
                </div>

                {/* URL + Copia */}
                <div className="flex items-center gap-2 border border-[#E5E7E0] bg-[#FFFFFF] px-4 py-3" data-testid="public-url-box">
                  <code className="flex-1 text-sm truncate">{publicListingUrl}</code>
                  <button onClick={copyPublicLink} className="flex-shrink-0 px-3 py-1.5 border border-[#7C8B6E] text-[#7C8B6E] hover:bg-[#7C8B6E] hover:text-white transition-colors text-sm" data-testid="copy-public-url">
                    {linkCopied ? <><Check size={14} /> Copiato</> : <><Copy size={14} /> Copia</>}
                  </button>
                </div>

                {/* Azioni · 4 bottoni compatti */}
                <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-2">
                  <a href={publicListingUrl} target="_blank" rel="noreferrer" className="btn-outline text-sm justify-center" data-testid="view-public-listing">
                    <Eye size={14} /> Anteprima
                  </a>
                  <button onClick={downloadQr} className="btn-outline text-sm justify-center" data-testid="download-qr-btn">
                    <QrCode size={14} /> QR code
                  </button>
                  <button
                    onClick={downloadQrWa}
                    className="btn-outline text-sm justify-center"
                    data-testid="download-qr-wa-btn"
                    title="QR che apre WhatsApp con messaggio pre-fillato"
                  >
                    <WhatsappLogo size={14} weight="fill" className="text-[#25D366]" /> QR WhatsApp
                  </button>
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent("Ecco l'immobile: " + (publicShareUrl || publicListingUrl))}`}
                    target="_blank"
                    rel="noreferrer"
                    className="btn-outline text-sm justify-center"
                    data-testid="share-whatsapp"
                  >
                    <ArrowSquareOut size={12} /> Condividi
                  </a>
                </div>

                {/* iter v28 r42 — Rimosso hint "Pubblicalo sui portali":
                    la step "Pubblica" contiene già la tab Portali accanto,
                    nessuna necessità di redirect duplicato. */}

                {/* iter v28 r400 — User M1310: «nell'annuncio, quando
                    pubblico, mi selezioni chi vuole e io posso mandare a
                    tutti quelli che vogliono quell'annuncio». Bottone che
                    apre il modal BulkGreetings con i contatti pre-filtrati
                    dal match preferences (zona / tipologia / budget / m²). */}
                <NotifyInterestedClientsButton
                  pid={id}
                  shareUrl={publicShareUrl}
                  property={property}
                />

                {/* Elimina pagina — discreto */}
                <div className="mt-6 pt-5 border-t border-[#EAE7DF]">
                  <button
                    onClick={onDeleteListing}
                    className="text-[12px] text-[#9A968B] hover:text-red-600 font-medium inline-flex items-center gap-1.5 transition-colors"
                    data-testid="delete-listing-btn"
                  >
                    <Trash size={12} weight="bold" /> Elimina pagina pubblica
                  </button>
                </div>
              </>
            )}
        </div>

        {/* Come funziona · collassabile, in fondo */}
        {!listing?.exists && (
          <details className="mt-4 group" data-testid="how-it-works">
            <summary className="cursor-pointer list-none p-3 text-[12.5px] text-[#6B6A65] hover:text-[#1C1C1A] inline-flex items-center gap-2">
              <span className="text-[#7C8B6E]">›</span> Come funziona in 3 passi
            </summary>
            <ol className="mt-2 ml-5 space-y-1.5 text-[13px] text-[#1C1C1A]">
              <li><strong className="text-[#7C8B6E]">1.</strong> Attiva la pagina e condividi link/QR.</li>
              <li><strong className="text-[#7C8B6E]">2.</strong> I clienti aprono il link, vedono l'annuncio e ti lasciano i contatti.</li>
              <li><strong className="text-[#7C8B6E]">3.</strong> Ricevi i contatti nella scheda "Richieste".</li>
            </ol>
          </details>
        )}
      </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   PreviewFullscreenModal · iter v33 r65
   User (iPhone): «Se schiaccio "Apri vetrina in nuova scheda" mi apre
   Chrome esterno che dice non autenticato, poi mi riporta dentro l'app.
   Se non si riesce a fare fuori, mostrala dentro l'app la vetrina».
   Fix: dentro Capacitor iOS/Android l'apri-in-tab non funziona perche'
   Safari esterno non condivide token/localStorage con il WebView Capacitor
   (specialmente per /listing-preview auth-only). Sostituito con overlay
   fullscreen che monta l'iframe stessa origin — token e session vengono
   ereditati automaticamente. Sul web resta il target=_blank normale.
   ════════════════════════════════════════════════════════════════════ */
function PreviewFullscreenModal({ url, open, onClose }) {
  React.useEffect(() => {
    if (!open) return undefined;
    // Blocca lo scroll del body sotto la modal
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[100] bg-black flex flex-col"
      style={{ paddingTop: "env(safe-area-inset-top)", paddingBottom: "env(safe-area-inset-bottom)" }}
      data-testid="preview-fullscreen-modal"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-black text-white">
        <div className="text-[13px] font-semibold">Anteprima vetrina</div>
        <button
          type="button"
          onClick={onClose}
          className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-white/10 active:bg-white/20 transition-colors"
          aria-label="Chiudi anteprima"
          data-testid="preview-fullscreen-close"
        >
          <X size={20} weight="bold" />
        </button>
      </div>
      <div className="flex-1 bg-white overflow-hidden">
        <iframe
          key={`fs-${url}`}
          src={url}
          title="Anteprima vetrina a schermo intero"
          className="w-full h-full border-0"
          sandbox="allow-scripts allow-same-origin allow-popups allow-forms"
        />
      </div>
    </div>
  );
}


/* ════════════════════════════════════════════════════════════════════
   PublicListingPreview — iter v28 r216
   Anteprima live mobile/desktop della vetrina pubblica con iframe.
   ════════════════════════════════════════════════════════════════════ */
function PublicListingPreview({ url, bust: extBust }) {
  const [device, setDevice] = useState("mobile");
  const [localBust, setLocalBust] = useState(Date.now());
  const [fsOpen, setFsOpen] = useState(false);
  const bust = extBust ?? localBust;

  // iter v29 r117 · Fix caching aggressivo iframe.
  // Chrome desktop ogni tanto serviva una versione cached della vetrina
  // anche col query string `?t=...`. Aggiungo anche `&nocache=...` + ID
  // bust nel render per forzare un remount React → nuovo iframe vergine.
  const iframeUrl = `${url}${url.includes("?") ? "&" : "?"}t=${bust}&nocache=${bust}`;
  const isMobile = device === "mobile";

  return (
    <div className="mt-6 pt-5 border-t border-[#EAE7DF]" data-testid="public-listing-preview">
      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Eye size={14} weight="bold" className="text-[#7C8B6E]" />
          <span className="overline">Anteprima live</span>
        </div>
        <div className="flex items-center gap-1">
          <div className="inline-flex bg-[#F4F1EA] rounded-md p-0.5" role="tablist">
            <button
              type="button"
              onClick={() => setDevice("mobile")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${isMobile ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#9A968B] hover:text-[#1C1C1A]"}`}
              data-testid="preview-toggle-mobile"
              aria-pressed={isMobile}
            >
              <DeviceMobile size={12} weight="fill" /> Mobile
            </button>
            <button
              type="button"
              onClick={() => setDevice("desktop")}
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded text-[11px] font-semibold transition-all ${!isMobile ? "bg-white text-[#1C1C1A] shadow-sm" : "text-[#9A968B] hover:text-[#1C1C1A]"}`}
              data-testid="preview-toggle-desktop"
              aria-pressed={!isMobile}
            >
              <Desktop size={12} weight="fill" /> Desktop
            </button>
          </div>
          <button
            type="button"
            onClick={() => setLocalBust(Date.now())}
            className="p-1.5 text-[#9A968B] hover:text-[#1C1C1A] transition-colors"
            title="Ricarica anteprima"
            data-testid="preview-refresh"
            aria-label="Ricarica"
          >
            <ArrowClockwise size={14} weight="bold" />
          </button>
        </div>
      </div>

      <div className="flex justify-center">
        {isMobile ? (
          <div
            className="relative rounded-[32px] p-[2.5px]"
            style={{
              width: 200,
              height: 420,
              background: "linear-gradient(135deg, #2A2A28 0%, #1C1C1A 50%, #2F2F2D 100%)",
              boxShadow: "0 22px 44px -16px rgba(28,28,26,0.42), 0 6px 12px -6px rgba(28,28,26,0.22), inset 0 1px 0 rgba(255,255,255,0.06)",
            }}
            data-testid="preview-iphone-mockup"
          >
            {/* Dynamic Island iPhone 17 Pro proporzionata 200px */}
            <div className="absolute top-[8px] left-1/2 -translate-x-1/2 w-[70px] h-[18px] bg-black rounded-full z-20" />
            <div className="relative w-full h-full overflow-hidden rounded-[28px] bg-white">
              {/* Specular highlight */}
              <div className="absolute top-0 left-0 right-0 h-[30%] pointer-events-none z-[5]"
                   style={{ background: "linear-gradient(180deg, rgba(255,255,255,0.04), transparent)" }} />
              <iframe
                key={`mobile-${bust}`}
                src={iframeUrl}
                title="Anteprima vetrina mobile"
                className="border-0 absolute top-0 left-0"
                style={{ width: 390, height: 844, transform: "scale(0.48)", transformOrigin: "top left" }}
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            </div>
          </div>
        ) : (
          <div
            className="relative w-full max-w-md bg-[#E5E7E0] rounded-lg overflow-hidden shadow-[0_20px_40px_-20px_rgba(0,0,0,0.3)]"
            data-testid="preview-desktop-mockup"
          >
            <div className="aspect-[16/10] relative">
              <iframe
                key={`desktop-${bust}`}
                src={iframeUrl}
                title="Anteprima vetrina desktop"
                className="absolute inset-0 w-full h-full border-0"
                sandbox="allow-scripts allow-same-origin allow-popups"
              />
            </div>
          </div>
        )}
      </div>

      {/* iter v33 r65 · User: «apri in nuova scheda mi apre Chrome esterno
          che dice non autenticato». Su Capacitor iOS/Android il
          target=_blank apre Safari fuori dall'app: perde il token
          localStorage del WebView → /listing-preview auth-only fallisce.
          Fix: se isNative apri overlay fullscreen (stessa origin, token
          ereditato). Su web resta target=_blank normale. */}
      <div className="mt-3 text-center">
        {isNative ? (
          <button
            type="button"
            onClick={() => setFsOpen(true)}
            className="inline-flex items-center gap-1.5 text-[11.5px] text-[#5A6A4D] hover:text-[#1C1C1A] underline underline-offset-2"
            data-testid="preview-open-in-tab"
          >
            Apri vetrina a schermo intero
            <ArrowsOutSimple size={11} weight="bold" />
          </button>
        ) : (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-[11.5px] text-[#5A6A4D] hover:text-[#1C1C1A] underline underline-offset-2"
            data-testid="preview-open-in-tab"
          >
            Apri vetrina in nuova scheda
            <ArrowSquareOut size={11} weight="bold" />
          </a>
        )}
      </div>

      <p className="mt-3 text-[11px] text-[#9A968B] text-center leading-snug">
        L'anteprima si aggiorna automaticamente quando cambi stile o colore.
        {isNative ? " Tocca il tasto sopra per vederla a tutto schermo." : " Per testare l'esperienza reale, apri il link sul tuo cellulare."}
      </p>

      <PreviewFullscreenModal
        url={iframeUrl}
        open={fsOpen}
        onClose={() => setFsOpen(false)}
      />
    </div>
  );
}

// iter v28 r400 — User M1310: bottone "Notifica clienti interessati"
// che pesca i contatti con preferences compatibili (tipologia, zona,
// budget, m²) e apre BulkGreetingsModal con loro pre-selezionati.
// iter v28 r402 — Auto-open quando si arriva da home alert con ?notify=1
// iter v32 r49 — User M1448: stesso registro di "Annuncio della settimana"
// (formale "Lei", zero emoji, saluto orario, link `/api/share/{public_id}`
// così WhatsApp mostra l'anteprima OG). Prima passavamo `${origin}/p/${pid}`
// — `pid` era il property_id interno, non un public_id, e `/p/` è la rotta
// `LegacySlugRedirect` per gli slug agente → niente anteprima e 404.
// iter v32 r49d — Rimosso il "Buongiorno," iniziale: il backend usa il
// template `custom` ("Buongiorno {salutation}, {custom}") e inietta lui
// la salutation con nome+cognome di OGNI cliente (es. "Buongiorno Sig.
// Bianchi,"). Mettere "Buongiorno," anche qui creava doppione.
function _buildPrefillText({ property, shareUrl }) {
  const title = (property?.title || "un nuovo immobile").trim();
  const city = (property?.city || "").trim();
  const nb = (property?.neighborhood || "").trim();
  const location = [nb, city].filter(Boolean).join(", ");
  const price = property?.price;
  let priceStr = "";
  if (typeof price === "number" && price > 0) {
    if (price >= 1_000_000) {
      const v = price / 1_000_000;
      priceStr = `€ ${v.toFixed(1).replace(/\.0$/, "")}M`;
    } else if (price >= 1000) {
      priceStr = `€ ${Math.round(price / 1000)}.000`;
    } else {
      priceStr = `€ ${Math.round(price)}`;
    }
  }
  const locationClause = location ? ` in zona ${location}` : "";
  const body = `ho da poco pubblicato un immobile${locationClause} che potrebbe interessarLe.`;
  const detail = priceStr ? `${title} — ${priceStr}.` : `${title}.`;
  const closing = shareUrl
    ? "Le lascio qui sotto il link con foto e dettagli. Resto a disposizione per qualsiasi informazione."
    : "Se desidera maggiori informazioni o una visita, mi scriva pure su questa chat.";
  const lines = [body, detail, "", closing];
  if (shareUrl) lines.push("", shareUrl);
  return lines.join("\n");
}

function NotifyInterestedClientsButton({ pid, shareUrl, property }) {
  const [loading, setLoading] = useState(false);
  const [matches, setMatches] = useState(null); // null | array
  const [modalOpen, setModalOpen] = useState(false);

  const loadMatches = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/contacts/match-property/${pid}`);
      setMatches(data?.matches || []);
      if ((data?.matches || []).length === 0) {
        toast.message("Nessun cliente con preferenze compatibili", {
          description: "Imposta tipologia/zona/budget nelle preferenze dei tuoi contatti per usare questa funzione.",
        });
      } else {
        setModalOpen(true);
      }
    } catch {
      toast.error("Impossibile cercare i clienti interessati");
    } finally {
      setLoading(false);
    }
  };

  // iter v28 r402 — Auto-trigger se arriva con ?notify=1 (dal banner home /
  // push iOS). Rimuove il flag dall'URL per non riaprire al reload.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("notify") === "1") {
      loadMatches();
      params.delete("notify");
      const newQs = params.toString();
      window.history.replaceState({}, "", window.location.pathname + (newQs ? `?${newQs}` : "") + window.location.hash);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <div className="mt-6 p-4 bg-[#F1F4ED] border border-[#7C8B6E]/30 rounded-lg">
        <div className="flex items-start gap-3">
          <Users size={18} weight="bold" className="text-[#7C8B6E] mt-0.5 shrink-0" />
          <div className="flex-1 min-w-0">
            <h4 className="text-[14px] font-semibold text-[#1C1C1A]">Notifica i clienti interessati</h4>
            <p className="text-[12px] text-[#6B6A65] mt-0.5 leading-snug">
              Pesca dai tuoi contatti chi cerca questa tipologia, zona o fascia di prezzo, e invia un messaggio WhatsApp con il link dell'annuncio.
            </p>
            {matches !== null && (
              <div className="mt-2 text-[12px] text-[#5C6B4E] font-semibold">
                {matches.length === 0 ? "Nessun cliente compatibile per ora." : `${matches.length} cliente${matches.length === 1 ? "" : "i"} compatibile${matches.length === 1 ? "" : "i"}`}
              </div>
            )}
            <button
              onClick={loadMatches}
              disabled={loading}
              className="mt-3 inline-flex items-center gap-1.5 bg-[#7C8B6E] hover:bg-[#6B7A5D] text-white text-[12.5px] font-semibold px-3.5 py-1.5 rounded-md disabled:opacity-50"
              data-testid="notify-interested-btn"
            >
              <WhatsappLogo size={13} weight="fill" />
              {loading ? "Cerco…" : "Trova clienti interessati"}
            </button>
          </div>
        </div>
      </div>
      {modalOpen && matches && matches.length > 0 && (
        <BulkGreetingsModalLazy
          open={modalOpen}
          onClose={() => setModalOpen(false)}
          prefillContactIds={matches.map((m) => m.id)}
          prefillOccasion="custom"
          prefillCustomText={_buildPrefillText({ property, shareUrl })}
          prefillMatchInfo={Object.fromEntries(matches.map((m) => [m.id, m.match_reasons || []]))}
          // iter v32 r49d — Quando arriviamo qui, abbiamo già un immobile
          // specifico (pid) + reasons di match per ogni contatto. Passare
          // questi al modale fa sì che il backend personalizzi ogni
          // messaggio con LLM (1 cred ogni 10 contatti, paga l'agente).
          prefillPropertyId={pid}
          prefillPersonalize={true}
        />
      )}
    </>
  );
}

// Lazy wrapper per evitare circular import
function BulkGreetingsModalLazy(props) {
  const [Comp, setComp] = React.useState(null);
  React.useEffect(() => {
    import("@/components/BulkGreetingsModal").then((m) => setComp(() => m.default));
  }, []);
  if (!Comp) return null;
  return <Comp {...props} />;
}
